import { useGetAllContextPacks } from "@/api/studio/contexts/queries";
import { StudiesLayout } from "@/components/layouts";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { Card, CardBadge, CardBody, CardSkeleton } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronRightIcon, PlusIcon, Square2StackIcon } from "@heroicons/react/20/solid";
import { TextSkeleton } from "@/features/projects/components/text-skeleton";
import { useGetStudy, useUpdateStudy } from "@/api/studio/studies/queries";
import { useUpdateRoute } from "@/features/studies/hooks/use-update-route";
import { useSegment } from "@/hooks/use-segment";
import { useModalStore } from "@/stores/use-modal-store";
import { CreateContextStudyPackModal } from "@/features/contexts/modals/create-context-study-pack-modal";
import ENV from "@/utils/env";
import { useEditorStore } from "@/features/studies/stores/use-editor-store";
import { Pagination } from "@/components/ui/pagination";
import { DragonflyIcon } from "@/assets/icons";
import { ContextsEmptyState } from "@/features/contexts/components/contexts-empty-state";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";

/* Prop Types */
interface StudyAssetContextRouteProps {
  projectId: string;
  studyId: string;
}

/*
 * StudyAssetContextRoute component
 * User selects one or more contexts for a study
 */
export const StudyAssetContextRoute = ({ projectId, studyId }: StudyAssetContextRouteProps) => {
  /* 
    Update the route with the current step
    we set this to study-review due to us not having preProcessed data
    if the user is sent back to this step without going to study-review first
  */
  useUpdateRoute(projectId, studyId, "study-review");

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-context");

  /* Actions */
  const { open } = useModalStore((state) => state.modalActions);
  const { setContextPackIds } = useEditorStore((state) => state.editorActions);

  /* Queries */
  const { data: study, isLoading: isStudyLoading } = useGetStudy(projectId, studyId);
  const { mutateAsync: updateStudy } = useUpdateStudy();

  /* State */
  const contextPackIds = useEditorStore((state) => state.contextPackIds);
  // const [selectedContext, setSelectedContext] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(1);

  // we only show published contexts that are the same use case as the study
  const { data: allContextPacks, isLoading: isAllContextPacksLoading } = useGetAllContextPacks({
    query: "",
    view: "published",
    orderBy: "createdAt",
    sortBy: "desc",
    pageIndex: pageIndex,
    itemsPerPage: 10,
    useCaseIds: study?.useCase.id ? [study.useCase.id] : [],
  });

  /* Handlers */
  const handleNavigate = async () => {
    // Segment tracking
    segmentTrackEvent("Study Context Pack Selection Confirmed", {
      projectId,
      studyId,
      contextPacksSelected: contextPackIds.length > 0 ? 1 : 0,
      contextPacksAvailable: allContextPacks?.packs.length,
      contextPackId: contextPackIds[0],
    });

    // Persist the current context pack selection before navigating to reports.
    // updateStudy uses PATCH semantics, so spreading the study leaves other fields
    // intact; an empty array clears any previously saved selection.
    if (study) {
      try {
        await updateStudy({ projectId, studyId, backgroundUpdate: true, values: { ...study, contextPackIds } });
      } catch {
        // Failure toast is surfaced by the mutation's onError; navigate regardless so the user isn't blocked.
      }
    }

    // Navigate to the next step
    navigateToNext();
  };

  const handleSelect = (contextPackId: string) => {
    // save contexts to editor store as this is what the process step will use
    // if the same context pack is clicked, deselect it by setting to empty array
    if (contextPackIds.includes(contextPackId)) {
      setContextPackIds([]);
    } else {
      setContextPackIds([contextPackId]);
    }
  };

  const handleNewContext = () => {
    open(<CreateContextStudyPackModal projectId={projectId} studyId={studyId} useCaseId={study?.useCase.id} useCaseName={study?.useCase.name} />);
  };

  /* Side Effects */
  useEffect(() => {
    setContextPackIds(study?.contextPackIds || []);
  }, [study]);

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-context">
      <div className="flex justify-between gap-4 items-center">
        <h1>Would you like to test your assets in context?</h1>
        <Button variant={contextPackIds.length > 0 ? "primary" : undefined} onClick={handleNavigate}>
          {contextPackIds.length > 0 ? "Get reports" : "Skip for now"} <ChevronRightIcon className="size-3.5" />
        </Button>
      </div>
      <p>
        Get a predicted visibility score of your assets within one or more simulated environments.
        <a href="https://academy.dragonflyai.co/en/articles/10670481-contexts-library" rel="noreferrer" target="_blank" className="ml-1">
          Learn more
        </a>
      </p>

      <div className="flex items-center gap-3">
        {isAllContextPacksLoading ? (
          <TextSkeleton variant="h2" className="max-w-32" />
        ) : (
          <h2>
            {allContextPacks?.packs.length} context {allContextPacks?.totalItems === 1 ? "pack" : "packs"}
          </h2>
        )}
        <Button variant="primary" onClick={handleNewContext} disabled={isStudyLoading}>
          <PlusIcon className="size-4" />
          New context pack
        </Button>
      </div>
      {/* Body */}
      <div className="flex flex-row gap-4 flex-wrap">
        {isAllContextPacksLoading && Array.from({ length: 10 }).map((_, i) => <CardSkeleton key={i} />)}
        {!isAllContextPacksLoading && allContextPacks && allContextPacks.packs?.length === 0 && (
          <ContextsEmptyState
            onClick={handleNewContext}
            showTitle={false}
            emptyMessage={<h2 className="text-base-content">No available context pack for this use case yet...</h2>}
            className="mx-auto mt-auto"
          />
        )}
        {!isAllContextPacksLoading &&
          allContextPacks &&
          allContextPacks.packs?.length > 0 &&
          allContextPacks?.packs.map((contextPack) => (
            <Card
              key={contextPack.id}
              onClick={() => handleSelect(contextPack.id)}
              selected={contextPackIds.includes(contextPack.id)}
              imageUrl={`${ENV.API_PLATFORM_URL}/v3/contexts/${contextPack.firstContextId}/image?maxWidth=400&maxHeight=400`}
            >
              <CardBody className="relative gap-1.5" title={contextPack.name}>
                {!contextPack.isDragonfly && <p className="text-base-content/70">{contextPack.username}</p>}
                {contextPack.isDragonfly && <p className="text-base-content/70">Dragonfly</p>}
                <div className="flex align-center">
                  <Badge className="bg-base-200 px-2.5 mr-2.5">{contextPack.useCase.name}</Badge>
                  <Badge className="bg-base-200 px-2.5">
                    <Square2StackIcon className="size-3.5 mr-2" />
                    {contextPack.contextCount}
                  </Badge>
                </div>
              </CardBody>
              {contextPack.isDragonfly && (
                <CardBadge className="p-1 rounded-lg w-auto h-auto">
                  <DragonflyIcon className="size-4" />
                </CardBadge>
              )}
            </Card>
          ))}
      </div>

      {/* Pagination */}
      <Pagination currentPage={pageIndex} totalPages={allContextPacks?.maxPageIndex ?? 1} onPageChange={setPageIndex} className="pb-4" />
    </StudiesLayout>
  );
};
